import { Controller } from "@hotwired/stimulus"
import store from "@/store"

const FEATURE_FLAG = "listings-scroll-restore"
const SCROLL_KEY_PREFIX = "listings-scroll:"
const HIGHLIGHT_KEY_PREFIX = "listings-highlight:"
const HIGHLIGHT_CLASS = "animate-halo"

const storageKey = (prefix) => `${prefix}${window.location.pathname}${window.location.search}`

export default class extends Controller {
  connect() {
    this.unsubscribeStore = null
    this.unsubscribeFeatureFlags = null
    this.experimentVariant = null

    this.subscribeToFeatureFlag()
    this.restoreFromHistory()
  }

  disconnect() {
    this.unsubscribeStore?.()
    this.unsubscribeStore = null
    this.unsubscribeFeatureFlags?.()
    this.unsubscribeFeatureFlags = null
  }

  subscribeToFeatureFlag() {
    this.unsubscribeFeatureFlags = window.posthog?.onFeatureFlags(() => {
      this.experimentVariant = window.posthog.getFeatureFlag(FEATURE_FLAG)
    })
  }

  saveScrollPosition() {
    if (!this.experimentEnabled) return
    sessionStorage.setItem(storageKey(SCROLL_KEY_PREFIX), String(window.scrollY))
    // ListingPagination sets a global window.onpopstate on every paginate that
    // re-dispatches changePage. Turbo back-navigation also triggers popstate, so
    // the stale handler would fire a spurious second fetchListings on return.
    window.onpopstate = null
  }

  captureClickedListing(event) {
    if (!this.experimentEnabled) return
    const link = event.target.closest(".ListingGrid a[href]")
    if (!link) return
    sessionStorage.setItem(storageKey(HIGHLIGHT_KEY_PREFIX), link.getAttribute("href"))
  }

  // Wait for the first non-empty setListings (i.e. the grid has rendered), then jump
  // to the saved scroll position and highlight the card the user came from. The
  // pending subscription is cancellable so stale restores never apply to a later visit.
  restoreFromHistory() {
    this.unsubscribeStore?.()
    this.unsubscribeStore = null

    const savedScroll = sessionStorage.getItem(storageKey(SCROLL_KEY_PREFIX))
    const clickedHref = sessionStorage.getItem(storageKey(HIGHLIGHT_KEY_PREFIX))
    if (savedScroll == null && clickedHref == null) return

    sessionStorage.removeItem(storageKey(SCROLL_KEY_PREFIX))
    sessionStorage.removeItem(storageKey(HIGHLIGHT_KEY_PREFIX))
    const targetY = savedScroll == null ? null : parseInt(savedScroll, 10)

    this.unsubscribeStore = store.subscribe((mutation) => {
      if (mutation.type !== "filter/setListings" || mutation.payload.length === 0) return

      this.unsubscribeStore?.()
      this.unsubscribeStore = null
      // initListings waits on posthog.onFeatureFlags before fetchListings, so by the
      // time setListings fires, flags are loaded and experimentVariant is set.
      if (!this.experimentEnabled) return
      requestAnimationFrame(() => {
        if (targetY != null) window.scrollTo(0, targetY)
        if (clickedHref) this.highlightClickedListing(clickedHref)
      })
    })
  }

  highlightClickedListing(href) {
    const link = this.element.querySelector(`.ListingGrid a[href="${CSS.escape(href)}"]`)
    if (!link) return
    link.classList.add(HIGHLIGHT_CLASS)
  }

  get experimentEnabled() {
    return this.experimentVariant === "test"
  }
}
